/**
 * Tenant List — Platform admin table with status filters, search, and pagination.
 * @see Documentation/Specification/020_Definitions/application-agnostic/identity/Tenant.md
 */
import { useState } from 'react';
import { useGetList, useNavigate } from 'react-admin';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, TablePagination, Button, Avatar,
} from '@mui/material';
import { Business, Search, Add } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { TenantRecord, STATUS_COLORS } from './types';

export const TenantList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { data: result } = useGetList('tenants', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  const allTenants: TenantRecord[] = (result as any)?.tenants ?? (Array.isArray(result) ? result : []);
  const filtered = allTenants
    .filter((t) => !statusFilter || t.status === statusFilter)
    .filter((t) => !search || t.name.toLowerCase().includes(search.toLowerCase()));
  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allTenants.length,
    active: allTenants.filter((t) => t.status === 'Active').length,
    suspended: allTenants.filter((t) => t.status === 'Suspended').length,
    liquidated: allTenants.filter((t) => t.status === 'Liquidated').length,
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/tenants/create')}>
          New Tenant
        </Button>
      </Box>
      <ListPageHeader
        title="Tenants"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Suspended', value: String(stats.suspended), color: 'warning.main' },
          { label: 'Liquidated', value: String(stats.liquidated), color: 'grey.600' },
        ]}
      />

      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[null, 'Active', 'Suspended', 'Liquidated'].map((s) => (
            <Chip
              key={s ?? 'all'}
              label={s ?? 'All'}
              size="small"
              color={statusFilter === s ? 'primary' : 'default'}
              variant={statusFilter === s ? 'filled' : 'outlined'}
              onClick={() => { setStatusFilter(s); setPage(0); }}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}>
          <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
          <Box
            component="input"
            placeholder="Search tenants..."
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setPage(0); }}
            sx={{ border: 'none', outline: 'none', py: 0.75, fontSize: 14, width: 200, bgcolor: 'transparent' }}
          />
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Registration</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((tenant) => (
              <TableRow
                key={tenant.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/tenants/${tenant.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 12 }}>
                      {tenant.name?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{tenant.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tenant.slug}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {tenant.registrationNumber || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{tenant.contact?.email}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={tenant.status} size="small" color={STATUS_COLORS[tenant.status] ?? 'default'} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {tenant.metadata?.createdAt?.split('T')[0] ?? '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {paged.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Business sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No tenants found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
